import {
  Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { CoursesService } from 'src/app/services/courses.service';
import { SurveysService } from 'src/app/services/surveys.service';
import { FormattedCourse, formatCourse, isPendingCourseRequest } from '../../../../my-courses.utils';
import { AuthService } from 'src/app/services/auth.service';
import { NewTableData } from 'src/app/components/new-table/new-table.component';
import { RequirementsService } from 'src/app/services/requirements.service';
import { UpdateRequirementModalComponent } from '../update-requirement-modal/update-requirement-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { UpdateStatusModalComponent } from '../update-status-modal/update-status-modal.component';
import { CatalogCourseDetailsComponent } from 'src/app/routes/manage-courses/components/course-catalog/components/catalog-course-details/catalog-course-details.component';
import { Router } from '@angular/router';
import { GeneralDataCell, StatusDataCell } from 'src/app/components/new-table/table-cell/table-cell.component';
import { EntityService } from 'src/app/services/entity.service';

@Component({
  selector: 'app-courses-list',
  templateUrl: './courses-list.component.html',
  styleUrls: ['./courses-list.component.scss'],
})
export class CoursesListComponent implements OnInit, OnChanges, OnDestroy {
  @Input() user: any;
  @Input() selectedYearChoice: any;
  @Input() disableEditingDueToYear: boolean;
  private courseSubscription: Subscription;
  private courseRegistrationSubscription: Subscription;
  private moecsSubscription: Subscription;
  private userSubscription: Subscription;
  private permissionsSubscription: Subscription;
  courses: any;
  formattedCourses: FormattedCourse[] = [];
  userRequirements: any[] = [];
  isLoading = false;
  selectedItemsPerPage = 25;
  offset = 0;
  sortColumn = 'dateOffered';
  sortColumnDirection: 'ASC' | 'DESC' = 'ASC';
  private moecsRows: any[] | null = null;
  private moecsYearId: any = null;

  loggedInUser: any;
  loggedInUserPermission: any;
  entityTimezoneSubscription: Subscription;

  tableData: NewTableData = {
    title: 'Course List',
    rowStyling: 'default',
    noDataFoundMessage: 'No course registrations found.',
    noDataButtonText: 'Register for Courses',
    columnTitles: [
      {
        name: 'Course',
        sortable: true,
        sortByProp: 'courseName',
      },
      {
        name: 'Date & Time',
        sortable: true,
        sortByProp: 'dateOffered',
        isDefaultSort: true,
        sortDirection: 'ASC',
        // width: '175px',
      },
      {
        name: 'Location',
        sortable: true,
        sortByProp: 'location',
        // width: '175px',
      },
      {
        name: 'Requirement',
        sortable: true,
        sortByProp: 'requirement',
        // width: '175px',
      },
      {
        name: 'Hours',
        sortable: true,
        sortByProp: 'adjustedValue',
        // width: '114px',
        // align: 'center',
      },
      {
        name: 'Status',
        sortable: true,
        sortByProp: 'userCourseStatus',
        // width: '150px',
      },
    ],
    data: [],
    hasBurgerMenu: true,
    // hamburgerMenuWidth: '74px',
    burgerContent: [
      { content: 'View Course Details', eventName: 'viewDetails' },
      {
        content: 'Take Survey',
        eventName: 'takeSurvey',
        displayCondition: (itemId: number) => this.itemInSurveyStatus(itemId),
      },
      {
        content: 'Update Course Status',
        eventName: 'updateStatus',
        displayCondition: (itemId: number) => this.userCanUpdateStatus(itemId),
      },
      {
        content: 'Change Requirement',
        eventName: 'changeRequirement',
        displayCondition: (itemId: number) => this.userCanUpdateStatus(itemId),
      },
    ],

    meta: {
      totalItems: 0,
      itemsPerPage: 0,
      curPage: 0,
      totalPages: 0,
      usePagination: false,
      links: {
        prev: '',
        next: '',
        first: '',
        last: '',
        self: '',
      },
    },
  }


  constructor(
    public dialog: MatDialog,
    private courseService: CoursesService,
    private surveysService: SurveysService,
    private requirementService: RequirementsService,
    private AuthService: AuthService,
    private router: Router,
    private entityService: EntityService,
  ) { }

  private formatCoursesWithTimezone(): void {
    if (!this.courses?.rows) return;
    const tz = this.entityService.getLocalTimezoneWithFallback();
    this.formattedCourses = this.courses.rows
      .map((course: any) => formatCourse(course, tz))
      .filter((c: FormattedCourse) => !isPendingCourseRequest(c));
    this.updateTableData();
  }

  ngOnInit(): void {
    if (!this.entityService.getLocalTimezone()) {
      this.entityService.getEntity('Config');
      this.entityTimezoneSubscription = this.entityService.entityData$.subscribe((entity: any) => {
        if (!entity) return;
        if (this.courses?.rows) this.formatCoursesWithTimezone();
        this.entityTimezoneSubscription?.unsubscribe();
      });
    }

    this.userSubscription = this.AuthService.user$.subscribe((user: any) => {
      if (!user) return;
      this.loggedInUser = user;
    });

    this.permissionsSubscription = this.AuthService.permissions$.subscribe((permissions: any) => {
      if (!permissions) return;
      this.loggedInUserPermission = permissions;
    });

    this.courseSubscription = this.courseService.coursesData$.subscribe((courses: any) => {
      if (!courses) return;
      if (!courses.rows) { this.isLoading = false; return; }

      this.courses = courses;
      const tz = this.entityService.getLocalTimezoneWithFallback();
      this.formattedCourses = courses.rows
        .map((course: any) => formatCourse(course, tz))
        .filter((c: FormattedCourse) => !isPendingCourseRequest(c));

      if (!this.applyMoecsData()) {
        this.updateTableData();
      }
      this.isLoading = false;
    });

    this.requirementService.getUserRequirements(this.user.id, false).subscribe((reqRes: any) => {
      const userRequirements = reqRes.data.rows;

      this.userRequirements = userRequirements;
    });
  }

  updateTableData() {
    const courses = this.formattedCourses;
    const courseData = courses.map((course: FormattedCourse) => {
      const courseId = course.courseItemId;

      const courseCell: GeneralDataCell = {
        type: 'general',
        mainText: course.name,
        subText: courseId,
        tooltip: course.isMoecsExported ? 'This course has been exported to MOECS' : undefined,
      };
      const dateAndTimeCell: GeneralDataCell = {
        type: 'general',
        mainText: course.dateOffered || '-',
        subText: course.time || '-',
      }
      const locationCell: GeneralDataCell = {
        type: 'general',
        mainText: course.location,
        subText: course.room,
      };

      const requirementCell: GeneralDataCell = {
        type: 'general',
        mainText: '',
      };

      if (course.parentRequirement) {
        requirementCell.mainText = course.parentRequirement;
        requirementCell.subText = course.requirement;
      } else {
        requirementCell.mainText = course.requirement;
        requirementCell.subText = '-'
      }

      const hoursCell: GeneralDataCell = {
        type: 'general',
        mainText: course.hours || '-',
      };

      const statusCell: StatusDataCell = {
        type: 'status',
        statusKid: course.statusKid,
        statusDisplay: course.statusDisplay,
        statusType: 'courseRegistration',
        forceUppercase: true,
      };


      return {
        itemId: courseId,
        columnData: [
          courseCell,
          dateAndTimeCell,
          locationCell,
          requirementCell,
          hoursCell,
          statusCell,
        ],
      };
    });

    const meta: NewTableData['meta'] = {
      curPage: this.courses.pagination.curPage,
      itemsPerPage: this.courses.pagination.totalPerPage,
      links: this.courses.links,
      totalItems: this.courses.pagination.totalItems,
      totalPages: this.courses.pagination.totalPages,
      usePagination: true,
    }
    if (meta.totalPages > 1) {
      meta.usePagination = true;
    }

    this.tableData.meta = meta;
    this.tableData.data = courseData;


  }

  calculateOffset(page: number): number {
    return (page - 1) * this.selectedItemsPerPage;
  }

  // Pagination event handlers
  onPageChanged(page: number) {
    const offset = this.calculateOffset(page);
    this.offset = offset;
    this.searchForCourses()
  }

  onItemsPerPageChange(itemsPerPage: number) {
    this.selectedItemsPerPage = itemsPerPage;
    this.offset = 0;
    this.searchForCourses()
  }

  // paginationEvent = (pageDirection: string) => {
  //   this.courseService.getPagination(pageDirection);
  // }

  searchForCourses() {
    if (!this.selectedYearChoice) {
      return;
    }
    this.isLoading = true;

    this.courseService.getPDPlanCourseRegistrations(
      {
        userId: this.user.id,
        sortDirection: this.sortColumnDirection,
        sortColumn: this.sortColumn,
        includedAssociations: 'Survey',
        yearId: this.selectedYearChoice,
      },
      this.selectedItemsPerPage,
      this.offset,
    );

    // Only fetch MOECS data when the year changes; reuse the cache otherwise
    if (this.moecsYearId !== this.selectedYearChoice) {
      this.moecsRows = null;
      this.moecsSubscription?.unsubscribe();
      this.moecsYearId = this.selectedYearChoice;
      this.moecsSubscription = this.courseService.getMOECSReports({ yearId: this.selectedYearChoice })
        .subscribe({
          next: (moecsResponse: any) => {
            if (moecsResponse.statusCode === 1000 && moecsResponse.data?.rows) {
              this.moecsRows = moecsResponse.data.rows;
              this.applyMoecsData();
            } else {
              this.moecsYearId = null;
              console.error('MOECS report returned unexpected response', moecsResponse);
            }
          },
          error: (err) => {
            this.moecsYearId = null;
            console.error('MOECS report fetch failed', err);
          },
        });
    }
  }

  processMOECSData(moecsData: any[]) {
    // Create a map of courseItemId to export status
    const moecsExportMap = new Map();

    moecsData.forEach((course) => {
      if (course.Registrations && course.Registrations.length > 0) {
        course.Registrations.forEach((registration: any) => {
          if (registration.userId === this.user.id && registration.stateDocExportedFlag === 1) {
            moecsExportMap.set(course.courseItemId, {
              exported: true,
              exportDate: registration.stateDocExportedDate,
              exportIdentifier: registration.exportIdentifier,
            });
          }
        });
      }
    });

    // Update the courses data with MOECS export information
    if (this.courses && this.courses.rows) {
      this.courses.rows.forEach((course: any) => {
        const moecsInfo = moecsExportMap.get(course.courseItemId);
        if (moecsInfo) {
          course.isMoecsExported = true;
          course.moecsExportDate = moecsInfo.exportDate;
          course.moecsExportIdentifier = moecsInfo.exportIdentifier;
        } else {
          course.isMoecsExported = false;
        }
      });

      // Re-format courses with updated MOECS data
      const tz = this.entityService.getLocalTimezoneWithFallback();
      this.formattedCourses = this.courses.rows
        .map((course: any) => formatCourse(course, tz))
        .filter((c: FormattedCourse) => !isPendingCourseRequest(c));
      this.updateTableData();
    }
  }

  private applyMoecsData(): boolean {
    if (this.moecsRows && this.courses?.rows) {
      this.processMOECSData(this.moecsRows);
      return true;
    }
    return false;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['disableEditingDueToYear']) {
      this.tableData.hasBurgerMenu = !this.disableEditingDueToYear;
    }

    const year = changes['selectedYearChoice'];
    if (year && year.currentValue !== year.previousValue && this.selectedYearChoice) {
      this.searchForCourses();
    }
  }

  ngOnDestroy(): void {
    this.entityTimezoneSubscription?.unsubscribe();
    this.courseSubscription?.unsubscribe();
    this.moecsSubscription?.unsubscribe();
    this.userSubscription?.unsubscribe();
    this.permissionsSubscription?.unsubscribe();
  }

  // #region Modal Handlers
  openRequirementsModal = (registration: any) => {
    const data = {
      userRequirements: this.userRequirements,
      course: registration,
      selectedUser: this.user,
      selectedRequirementId: registration.selectedRequirementId,
    }
    // this.popup = '';
    const dialog = this.dialog.open(UpdateRequirementModalComponent, { data: data });

    dialog.afterClosed().subscribe((result: any) => {

      this.isLoading = true;
      this.searchForCourses();
    });
  }

  openUpdateStatusModal = (registration: any) => {
    const dialog = this.dialog.open(UpdateStatusModalComponent, { data: registration });
    dialog.afterClosed().subscribe((result: any) => {
      if (result) {
        this.isLoading = true;

        this.searchForCourses();
      }
    });
  }

  openCourseDetailsModal = (registration: any) => {
    this.dialog.open(CatalogCourseDetailsComponent, {
      data: {
        courseItemId: registration.courseItemId,
        userRequirements: this.user.Requirements,
        disableEditingDueToYear: this.disableEditingDueToYear,
      },
    })
  }

  /**
   * Returns true when the course has a future sessionStartDate and is not async/anytime.
   * Used to block validation-related actions from PD Plan views before the session starts.
   */
  private isFutureSessionCourse(registrationId: number): boolean {
    const course = this.courses?.rows?.find((c: any) => c.courseItemId == registrationId);
    if (!course) return false;
    const option = (course?.preRecordedOption ?? course?.CourseItem?.preRecordedOption ?? '').toUpperCase();
    if (option === 'ASYNC' || option === 'ANYTIME') return false;
    const sessionStart = course?.sessionStartDate ?? course?.CourseItem?.sessionStartDate;
    if (!sessionStart) return false;
    return new Date() < new Date(sessionStart);
  }

  itemInSurveyStatus(registrationId: number) {
    const course = this.courses.rows.find((course: any) => {
      return course.courseItemId == registrationId;
    });

    if (this.isFutureSessionCourse(registrationId)) return false;
    return course.currentStatusKid === 'TAKE_SURVEY';
  }

  userCanUpdateStatus(registrationId: number) {
    const course = this.courses.rows.find((course: any) => {
      return course.courseItemId == registrationId;
    });
    const loggedInUserHasPermission = this.loggedInUserPermission.VALIDATE_ATTENDANCE;
    const loggedInUserIsOwner = course.userId === this.loggedInUser.id;

    return loggedInUserIsOwner || loggedInUserHasPermission;
  }
  // #endregion

  // #region Event Handlers
  handleRowClicked(itemId: number) {
    const registration = this.formattedCourses.find((course: FormattedCourse) => course.courseItemId === itemId);

    const currentStatus = registration.statusKid;
    if (currentStatus === 'TAKE_SURVEY' && !this.isFutureSessionCourse(itemId)) {
      return this.updateSurveyStatus(registration.id);
    }

    this.openCourseDetailsModal(registration);
  }

  handleBurgerMenuClicked({ eventName, itemId }: { eventName: string, itemId: number }) {
    const course = this.formattedCourses.find((course: FormattedCourse) => course.courseItemId === itemId);
    if (eventName === 'changeRequirement') {
      this.openRequirementsModal(course);
    }

    if (eventName === 'updateStatus') {
      this.openUpdateStatusModal(course);
    }

    if (eventName === 'viewDetails') {
      this.openCourseDetailsModal(course);
    }

    if (eventName === 'takeSurvey' && !this.isFutureSessionCourse(itemId)) {
      this.updateSurveyStatus(course.id);
    }
  }

  updateSurveyStatus = (registrationId: number) => {
    const course = this.courses.rows.find((course: any) => course.id === registrationId);
    const url = course?.Course?.Survey.url;

    if (!!url) {
      window.open(url, '_blank');
    }

    this.courseRegistrationSubscription = this.courseService.updateFromSurvey(course.id).subscribe({
      next: () => {
        this.searchForCourses();
      },
      error: (error: any) => {
        console.error(error);
      },
    });
  }

  handleSortClicked({ columnName, sortDirection }: { columnName: string; sortDirection: 'ASC' | 'DESC' }) {
    this.sortColumn = columnName;
    this.sortColumnDirection = sortDirection;
    this.offset = 0;

    this.searchForCourses()
  }

  handleNoDataButtonClicked() {
    // here we want to redirect to the course catalog
    this.router.navigate(['/manage-courses/course-catalog'])
  }
  // #endregion
}
